import { z } from "zod";

/**
 * Shared remittance line shapes.
 *
 * These schemas travel unchanged across three steps —
 * `normalize_remittance_document` produces them, `create_pending_payment`
 * passes them through, and `resolve_invoice_references` consumes them — so they
 * live here rather than being redeclared in each step file. A field added to
 * one copy but not the others is silently dropped mid-pipeline, which is
 * exactly how `additionalIdentifier` would have been lost.
 */

/**
 * One invoice the customer claims to be paying, as stated on the remittance.
 *
 * Everything here is customer-asserted and unverified: `invoiceNumber` is
 * whatever string the document showed, which routinely fails to resolve (a
 * mangled number, another system's reference, a plain typo).
 */
export const invoiceReferenceSchema = z.object({
  /** Empty when the line stated none and none could be recovered. */
  invoiceNumber: z.string(),
  /**
   * True when the line stated no invoice number and `invoiceNumber` was read
   * from its other references. Weaker than a stated number, so the resolver
   * confirms it like a secondary identifier rather than trusting it.
   */
  invoiceNumberRecovered: z.boolean().optional(),
  appliedAmount: z.number().optional(),
  grossAmount: z.number().optional(),
  discountAmount: z.number().optional(),
  adjustmentAmount: z.number().optional(),
  /**
   * A secondary identifier stated alongside the invoice number — in practice a
   * customer PO pulled out of the line's remarks ("PO 2192312"). It exists
   * because deduction lines are the ones most likely to carry a nonsense
   * invoice number while still naming a resolvable PO, which is the only way
   * to tie the deduction back to the invoice it was withheld from.
   *
   * Free-form and unparsed at this layer: the resolver tokenizes it rather than
   * assuming a format, since the extraction has no schema for what a given
   * customer writes in a remarks column.
   */
  additionalIdentifier: z.string().nullable().optional(),
});

/**
 * A standalone (general) deduction the customer withheld that isn't tied to a
 * specific invoice reference — e.g. a freight/shortage claim stated on the
 * remittance as a lump sum. Invoice-tied deductions live on the
 * `discountAmount`/`adjustmentAmount` of an invoice reference instead.
 */
export const deductionSchema = z.object({
  amount: z.number(),
  reason: z.string().optional(),
});

/**
 * A stated total its line items do not add up to (beyond a cent, after
 * standalone deductions): a sign that extraction dropped or misread a line.
 * Amounts are decimal strings.
 */
export const lineSumMismatchSchema = z.object({
  statedTotal: z.string(),
  lineSum: z.string(),
  /** statedTotal minus lineSum. */
  difference: z.string(),
});

export type InvoiceReference = z.infer<typeof invoiceReferenceSchema>;
export type RemittanceDeduction = z.infer<typeof deductionSchema>;
export type LineSumMismatch = z.infer<typeof lineSumMismatchSchema>;

/**
 * The normalized remittance payload — every data field the downstream
 * `create_pending_payment` step needs, in one socket. Produced by
 * `normalize_remittance_document` from a document's extraction, or handed in
 * directly by a deterministic source through `submit()` (`preNormalized`).
 */
export const RemittanceDocSchema = z.object({
  paymentAmount: z.number(),
  paymentDate: z.string().optional(),
  paymentMethod: z.string().optional(),
  referenceNumber: z.string().optional(),
  customerName: z.string().optional(),
  customerEmailAddresses: z.array(z.string()).default([]),
  invoiceReferences: z.array(invoiceReferenceSchema).default([]),
  deductions: z.array(deductionSchema).optional(),
  /** Set when the line items do not reconcile to the stated total; for review. */
  lineSumMismatch: lineSumMismatchSchema.optional(),
});

export type RemittanceDoc = z.infer<typeof RemittanceDocSchema>;

/**
 * Remittance intake channels. Mirrors the backend `RemittanceAdviceChannel`
 * enum and the `ar_remittance_advice.channel` check constraint; a channel is
 * added here only together with its migration.
 */
export const REMITTANCE_INTAKE_CHANNELS = [
  "email",
  "edi",
  "portal",
  "manual",
  "bank_memo",
] as const;

/**
 * Where a remittance came from. Every source (email today; portal, bank memo,
 * EDI next) describes its intake with this object, and
 * `create_pending_payment` records the advice from it without knowing which
 * source produced it.
 */
export const remittanceProvenanceSchema = z.object({
  channel: z
    .enum(REMITTANCE_INTAKE_CHANNELS)
    .describe("Source type; stored as ar_remittance_advice.channel"),
  rawReference: z
    .string()
    .nullable()
    .describe(
      "Stable source-system intake id: the dedup key and advisory-lock root (e.g. `<graphMessageId>#<file>`)",
    ),
  receivedAt: z.iso
    .datetime({ offset: true })
    .describe("When the remittance reached us; the date fallback for matching"),
  sender: z
    .string()
    .nullable()
    .optional()
    .describe(
      "Lowercased sender email address, a customer-identification hint",
    ),
  inboundEmailId: z
    .uuid()
    .optional()
    .describe("Set for email intake; links the advice back to its email"),
  bankTransactionId: z
    .uuid()
    .optional()
    .describe("The receipt this remittance explains, when the source knows it"),
  customerId: z
    .uuid()
    .optional()
    .describe("Customer hint from the source (e.g. a portal binding)"),
  parentRunId: z
    .uuid()
    .optional()
    .describe("Workflow run that produced this intake, for tracing"),
});

export type RemittanceProvenance = z.infer<typeof remittanceProvenanceSchema>;

/**
 * How a submitted remittance document gets its fields: `extract` runs the
 * document through extraction; `pre_normalized` skips it because the source
 * already parsed the remittance deterministically (820/CTX addenda).
 */
export const REMITTANCE_EXTRACTION_MODES = [
  "extract",
  "pre_normalized",
] as const;

export type RemittanceExtractionMode =
  (typeof REMITTANCE_EXTRACTION_MODES)[number];
